/**
 * Oeffentliche Startseite (ohne Login) — Produkt-Positionierung als Multi-User-SaaS
 * (Anforderung TikTok/Google App-Review): beschreibt klar, was InvictaFlow ist,
 * bietet Registrierung & Login, verlinkt Datenschutz, Nutzungsbedingungen & Impressum.
 */
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Megaphone, CalendarCheck, BarChart3, Users, Sparkles, Globe, ArrowRight, ShieldCheck, Mail,
} from "lucide-react";

const FEATURES = [
  { icon: Megaphone, title: "Social-Media-Publishing", text: "Beiträge und Videos für TikTok, Facebook, Instagram, LinkedIn und Pinterest zentral erstellen, freigeben und automatisch veröffentlichen — inklusive TikTok Direct Post." },
  { icon: Sparkles, title: "KI-gestützte Inhalte", text: "Texte, Bilder und Reel-Videos werden KI-gestützt erstellt — immer im Markenstil des jeweiligen Unternehmens." },
  { icon: BarChart3, title: "Kampagnen & Werbung", text: "Google-Ads- und Meta-Kampagnen verwalten: Budgets, Keywords und Conversion-Tracking an einem Ort." },
  { icon: Globe, title: "Website-Analytics & SEO", text: "Besucher, Sessions, Einstiegsseiten und Keyword-Rankings pro Unternehmen im Blick behalten." },
  { icon: Users, title: "Lead-Management", text: "Kundenanfragen aus Formularen und Lead Ads zentral erfassen, qualifizieren und bearbeiten." },
  { icon: CalendarCheck, title: "Planung & Freigabe", text: "Redaktionskalender mit Freigabe-Workflow — jedes Team behält die Kontrolle über seine Inhalte." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="landing-page">
      {/* Header */}
      <header className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/invictaflow-logo.png" alt="InvictaFlow Logo" className="w-9 h-9 rounded-lg" />
          <span className="font-bold text-lg">InvictaFlow</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button variant="outline" size="sm" data-testid="landing-login-btn">Anmelden</Button>
          </Link>
          <Link to="/registrieren">
            <Button size="sm" data-testid="landing-register-btn">Kostenlos registrieren</Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-12 pb-16 text-center">
        <img src="/invictaflow-logo.png" alt="InvictaFlow" className="w-24 h-24 mx-auto mb-6 rounded-2xl shadow-sm" />
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">InvictaFlow</h1>
        <p className="mt-3 text-base md:text-lg font-medium text-slate-700">
          Die Social-Media- &amp; Marketing-Plattform für KMU und Agenturen in der Schweiz
        </p>
        <p className="mt-4 text-base text-slate-600 max-w-2xl mx-auto" data-testid="landing-description">
          Mit InvictaFlow planen, erstellen und veröffentlichen Unternehmen und Agenturen ihre
          Social-Media-Inhalte, steuern Werbekampagnen und behalten Website-Analysen und Kundenanfragen
          im Blick — für beliebig viele Marken und Teams, KI-unterstützt und mit klaren Freigabeprozessen.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link to="/registrieren">
            <Button className="gap-1.5" data-testid="landing-cta-register">
              Jetzt kostenlos starten <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="outline" className="gap-1.5" data-testid="landing-cta-login">
              Anmelden
            </Button>
          </Link>
          <a href="mailto:info@invictax.ch?subject=InvictaFlow%20Anfrage">
            <Button variant="ghost" className="gap-1.5 text-slate-500" data-testid="landing-contact-btn">
              <Mail className="w-4 h-4" /> Kontakt
            </Button>
          </a>
        </div>
      </section>

      {/* Features */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-14">
          <h2 className="text-lg font-semibold text-center mb-10">Was InvictaFlow kann</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="bg-white rounded-xl border border-slate-200 p-5">
                <f.icon className="w-6 h-6 text-[#2563EB] mb-3" />
                <h3 className="font-semibold text-sm">{f.title}</h3>
                <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / Integrations */}
      <section className="max-w-5xl mx-auto px-6 py-12 text-center">
        <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold mb-3">Verbundene Dienste</p>
        <p className="text-sm text-slate-500">
          TikTok · Meta (Facebook &amp; Instagram) · LinkedIn · Pinterest · Google Ads · SEMrush · OpenAI · Google Gemini
        </p>
        <p className="mt-4 text-xs text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          Die Nutzung von Google-API-Daten folgt der Google API Services User Data Policy (Limited Use).
        </p>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>© {new Date().getFullYear()} InvictaX Group GmbH · Bahnhofstrasse 75 · 8620 Wetzikon · Schweiz</span>
          <span className="flex gap-4">
            <Link to="/impressum" className="underline hover:text-slate-600" data-testid="landing-impressum-link">Impressum</Link>
            <Link to="/privacy" className="underline hover:text-slate-600" data-testid="landing-privacy-link">Datenschutzerklärung</Link>
            <Link to="/terms" className="underline hover:text-slate-600" data-testid="landing-terms-link">Nutzungsbedingungen</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
